import React, { useEffect, useRef } from "react";
import isEmpty from "lodash/isEmpty";
import { useRouter } from "next/router";

import getDispensariesV3 from "api/requests/consumerApi/getDispensariesV3";
import { getDeliveryCopyBlock } from "api/requests/directus/getDeliveryCopyBlock";
import { getMetaData } from "api/requests/directus/getMetaData";
import getCityStateGeocode, {
  CityInfo,
} from "api/requests/getCityStateGeocode";
import getDeliveryCities from "api/requests/getDeliveryCities";
import getFinderCitiesNearMe from "api/requests/getFinderCitiesNearMe";
import { getUserLocationData } from "api/requests/getUserLocation";
import { Action, Category } from "constants/events";
import { stateData } from "constants/states";
import { City } from "custom-types/CitiesNearMe";
import {
  DeliveryCities,
  DeliveryCopyBlock as DeliveryCopyBlockType,
  FAQ,
} from "custom-types/Directus";
import { DispensaryV3 } from "custom-types/DispensariesV3";
import { MenuItem } from "custom-types/MenuItem";
import { RetailType } from "custom-types/Store";
import { useEventTracker } from "hooks/useEventTracker";
import { useSelector } from "redux/hooks";
import {
  getCoordinates,
  getIsValidStreetAddress,
} from "redux/selectors/location";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import { getStateOrProvinceLongName } from "utils/getStatesForCountry";
import isValidStreetAddress from "utils/isValidStreetAddress";
import { getCountryCodeHeader } from "utils/requestContext";
import slugify from "utils/slugifyURLComponent";

import AddressCaptureModal from "components/AddressCaptureModal";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import DealsTakeover from "components/DealsTakeover";
import DeliveryCopyBlock from "components/DeliveryGateway/DeliveryCopyBlock/DeliveryCopyBlock";
import DeliveryFromLeafly from "components/DeliveryGateway/DeliveryFromLeafly";
import DeliveryGatewayHero from "components/DeliveryGateway/DeliveryGatewayHero";
import DeliveryHead from "components/DeliveryGateway/DeliveryHead";
import PreaddressDispensaryCards from "components/DeliveryGateway/PreaddressDispensaryCards";
import PreaddressMap from "components/DeliveryGateway/PreaddressMap";
import ReadyForDelivery from "components/DeliveryGateway/ReadyForDelivery";
import DispensaryFulfillmentInfo from "components/DispensaryFulfillmentInfo";
import FAQs from "components/FAQ";
import PopularCities from "components/PopularCities/PopularCities";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";
import TrackImpression from "components/TrackImpression";

/**
 * Use a large take to load "all" dispensaries.
 * 100 is a secret limit in finder-service; larger values will be rejected.
 * Menu items will only be loaded for visible dispos.
 */
const MAX_DISPENSARY_TAKE = 50;

/** finder-service returns a slightly different data format than Directus,
 *  this will add name and link so we can plug into the PopularCities card
 */
const formatCities = (cities: City[]) =>
  cities.map((item) => {
    const longName = getStateOrProvinceLongName(item.state);
    return {
      ...item,
      count: String(item.count),
      link: longName
        ? `/delivery/${slugify(longName)}/${slugify(item.city)}`
        : "/delivery",
      name: `${item.city}, ${item.state}`,
    };
  });

const stateToCity = (
  data?: (typeof stateData)[keyof typeof stateData],
): CityInfo | undefined => {
  if (!data) {
    return undefined;
  }
  const { coordinates, country_code, radius, slug, state, stateCode } = data;
  return coordinates && country_code && radius && state && stateCode
    ? {
        boundedRadius: radius,
        coordinates,
        countryCode: country_code,
        state: stateCode,
        stateLong: state,
        stateSlug: slug,
      }
    : undefined;
};

type DeliveryPageProps = {
  dispensaries: DispensaryV3[];
  metadata: FAQ | null;
  cities: DeliveryCities["delivery_cities"];
  copyBlocks: DeliveryCopyBlockType["copy_block"];
  geoCityResult?: CityInfo;
};

const DeliveryPage: React.FC<DeliveryPageProps> = ({
  dispensaries,
  metadata,
  cities,
  copyBlocks,
  geoCityResult,
}) => {
  const { reload, push } = useRouter();
  const { publishEvent, trackEvent } = useEventTracker();

  const [modalVisible, setModalVisible] = React.useState(false);

  const modalReturnRef = React.createRef<HTMLButtonElement>();

  const coordinates = useSelector(getCoordinates);
  const isValidAddress = useSelector(getIsValidStreetAddress);

  const hasGeoState = geoCityResult?.stateLong && geoCityResult?.stateSlug;
  const hasGeoCity = geoCityResult?.city && geoCityResult?.citySlug;

  // * The following pieces of state are a type of semaphore for when a user clicks on
  // * a previewed retailer on this screen. Instead of redirecting post-address to /shop,
  // * we instead want to attempt to show the user that retailers menu.
  // * See: components/DeliveryGateway/PreaddressDispensaryCards/PreaddressDispensaryCards.tsx
  // * dispensaryLoadLock: prevents auto-redirects on this page when a valid delivery address is detected.
  const [dispensaryLoadLock, setDispensaryLoadLock] =
    React.useState<boolean>(false);
  // * Destructuring prevents hook re-renders.
  const { lat, lon } = coordinates;
  const firstUpdate = useRef(true);

  // * Effect to refresh retailer preview when location changes.
  useEffect(() => {
    // * Ignore the first render
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    // * Reload if not navigating to either /shop or /dispensary-info
    if (!isValidAddress && !dispensaryLoadLock) {
      reload();
    }
  }, [lat, lon, dispensaryLoadLock, isValidAddress]);

  // * attemptedDispensaryName: state used for "[retailer] delivery not available" alerts when redirecting to /shop.
  const [attemptedDispensaryName, setAttemptedDispensaryName] = React.useState<
    string | undefined
  >();
  // * Redirect users to /shop once they have provided us with a valid address!
  // * DON'T do this if the user has queried for delivery from a specific dispensary
  // * previewed on the landing page (dispensaryLoadLock)
  useEffect(() => {
    if (isValidAddress && !dispensaryLoadLock) {
      // * Needs to be a full page redirect I believe, since /shop
      // * relies on getServerSideProps currently.
      const params = new URLSearchParams({
        fulfillmentPreference: "delivery",
        ...(attemptedDispensaryName
          ? { attemptedRetailerName: attemptedDispensaryName }
          : {}),
      });

      push(`/shop?${params.toString()}`);
    }
  }, [isValidAddress, attemptedDispensaryName, dispensaryLoadLock]);

  const sharedRecentlyViewedProps = (menuItem?: MenuItem) => ({
    brandId: menuItem?.brand?.id,
    category: Category.deliveryGateFlow,
    dispensaryId: menuItem?.dispensaryId,
    label: "recently viewed menu item card",
    menuItemId: menuItem?.id,
    productId: menuItem?.product?.id,
  });

  return (
    <>
      <DeliveryHead metadata={metadata} geoCityResult={geoCityResult} />

      <DeliveryGatewayHero />

      {!!dispensaries.length && (
        <PreaddressDispensaryCards
          className="my-section"
          dispensaries={dispensaries}
          geoCityResult={geoCityResult}
          headingOverride={metadata?.h1_override}
          setAttemptedDispensaryName={setAttemptedDispensaryName}
          setDispensaryLoadLock={setDispensaryLoadLock}
        />
      )}

      <DeliveryCopyBlock className="my-section" copyBlocks={copyBlocks} />

      {!!dispensaries.length && (
        <PreaddressMap
          deliveryCount={dispensaries.length}
          cityInfo={geoCityResult}
          className="my-section"
        />
      )}

      <DeliveryFromLeafly className="my-section" showCTA={true} />

      {!!dispensaries.length && (
        <>
          <DealsTakeover
            ctaLabel="shop deals in your area"
            gaCategory={Category.deliveryGateFlow}
            gaLabel="deals block"
            handleClick={() => setModalVisible(true)}
            hideLocation={true}
            signupSource="delivery_deals"
          />
          <AddressCaptureModal
            isVisible={modalVisible}
            onDismissed={() => setModalVisible(false)}
            returnFocusRef={modalReturnRef}
          />
        </>
      )}

      {metadata?.faqs?.length ? (
        <TrackImpression
          category={Category.deliveryGateFlow}
          label="faq block"
          className="my-section empty:hidden"
        >
          <FAQs
            data={metadata?.faqs}
            onEntryClick={() =>
              trackEvent(Category.deliveryGateFlow, Action.click, "faq block")
            }
            initialShowPanel={true}
          />
        </TrackImpression>
      ) : (
        <> </>
      )}

      <ReadyForDelivery />

      <PopularCities
        cities={cities}
        title="Weed delivery near you"
        subtitle="Shop Leafly delivery for local, legal weed, anywhere you are."
        className="my-section"
        gaCategory={Category.deliveryGateFlow}
        gaLabel="location block"
      />

      <RecentlyViewedMenuItems
        className="my-section"
        carouselClassName="container"
        onCardClick={(menuItem: MenuItem) =>
          publishEvent({
            action: Action.Click,
            ...sharedRecentlyViewedProps(menuItem),
          })
        }
        onCardImpression={(menuItem: MenuItem) =>
          publishEvent({
            action: Action.impression,
            nonInteractionEvent: true,
            ...sharedRecentlyViewedProps(menuItem),
          })
        }
        menuItemProps={{
          renderFulfillmentInfo: (dispensary) => (
            <DispensaryFulfillmentInfo dispensary={dispensary} />
          ),
          showFulfillment: false,
        }}
      />

      {hasGeoState && (
        <Breadcrumbs>
          <Breadcrumb href="/delivery">Delivery</Breadcrumb>
          <Breadcrumb
            currentLocation={!hasGeoCity}
            href={`/delivery/${geoCityResult.stateSlug}`}
          >
            {geoCityResult.stateLong}
          </Breadcrumb>
          {hasGeoCity && (
            <Breadcrumb
              currentLocation={true}
              href={`/delivery/${geoCityResult.stateSlug}/${geoCityResult.citySlug}`}
            >
              {geoCityResult.city}
            </Breadcrumb>
          )}
        </Breadcrumbs>
      )}
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<DeliveryPageProps>(
  async (context) => {
    const slugs = (context.query.slug as string[]) || [];

    if (slugs.length > 2) {
      return {
        notFound: true,
      };
    }

    const countryCode = getCountryCodeHeader(context.req.headers);
    const location = await getUserLocationData(countryCode, context);

    // * Users who already have a valid address can be delivered to!
    // * We send them to /shop so they can begin shopping immediately :)
    const isValidAddress = isValidStreetAddress(location);
    if (isValidAddress) {
      return {
        redirect: {
          destination: `/shop?fulfillmentPreference=delivery`,
          permanent: false,
        },
      };
    }

    const stateSlug = slugs.length >= 1 && slugs[0];
    const citySlug = slugs.length === 2 && slugs[1];

    let geoCityResult;
    if (stateSlug && citySlug) {
      geoCityResult = await getCityStateGeocode({
        city: String(citySlug),
        state: String(stateSlug),
      });
    } else if (stateSlug && !citySlug) {
      // Prefer hand-curated results if available
      geoCityResult = stateToCity(
        stateData[String(stateSlug).toLowerCase() as keyof typeof stateData],
      );

      if (!geoCityResult) {
        geoCityResult = await getCityStateGeocode({
          city: "",
          state: String(stateSlug),
        });
      }
    }

    const isSEODeliveryQuery = !!geoCityResult;
    const coords = geoCityResult
      ? geoCityResult.coordinates
      : location?.coordinates
        ? {
            lat: location.coordinates.latitude,
            lon: location.coordinates.longitude,
          }
        : undefined;

    const path = `/delivery${stateSlug ? `/${stateSlug}` : ""}${
      citySlug ? `/${citySlug}` : ""
    }`;

    const [metadata = null, dispensariesData, deliveryCities, copyBlocks] =
      await Promise.all([
        getMetaData("faq", path, countryCode),
        getDispensariesV3({
          ...coords,
          ...(isSEODeliveryQuery && geoCityResult?.boundedRadius
            ? {
                radius: `${geoCityResult.boundedRadius}mi`,
              }
            : {}),
          deliversToPoint: !isSEODeliveryQuery,
          flags: ["delivery", "dispensary", countryCode],
          merchUnitName: "delivery-platinum-placement",
          sort:
            (context.query.dispensarySort &&
              String(context.query.dispensarySort)) ||
            "default",
          take: MAX_DISPENSARY_TAKE,
        }),
        getDeliveryCities(countryCode, citySlug || stateSlug ? path : null),
        getDeliveryCopyBlock("delivery_copy_block", path, countryCode),
      ]);

    let cities: DeliveryCities["delivery_cities"] = [];
    if (!deliveryCities.delivery_cities?.length) {
      const citiesNearby = await getFinderCitiesNearMe({
        countryCode,
        delivery_enabled: true,
        location: {
          coordinates: coords,
        },
        retailType: "dispensary" as RetailType,
      });

      cities = formatCities(citiesNearby) || [];
    } else {
      cities = deliveryCities.delivery_cities;
    }

    const dispensaries = [
      ...dispensariesData.sponsoredStores,
      ...dispensariesData.stores,
    ];

    return {
      props: {
        cities,
        copyBlocks: copyBlocks?.copy_block || [],
        disableFixedHeader: true, // passed to _app
        dispensaries,
        ...(!isEmpty(geoCityResult) && { geoCityResult }),
        metadata,
      },
    };
  },
);

export default DeliveryPage;
